import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Archive, Loader2, RotateCcw, ExternalLink, Trash2, Eye, Search, FileText,
  Image as ImageIcon, Video as VideoIcon, BookOpen, Megaphone, Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import { ViewPostDialog } from '../components/ViewPostDialog';
import { ClientFilterPill, ClientBadge } from '../components/ClientFilterPill';
import { useClients } from '../hooks/useClients';

const TYPE_ICONS = {
  blog: BookOpen,
  ad: Megaphone,
  email: Mail,
  carousel: ImageIcon,
  video_script: VideoIcon,
};

const PLATFORM_COLORS = {
  linkedin: 'bg-sky-50 text-sky-700 border-sky-200',
  instagram: 'bg-pink-50 text-pink-700 border-pink-200',
  facebook: 'bg-blue-50 text-blue-700 border-blue-200',
  twitter: 'bg-zinc-100 text-zinc-700 border-zinc-300',
  x: 'bg-zinc-100 text-zinc-700 border-zinc-300',
  youtube: 'bg-red-50 text-red-700 border-red-200',
  tiktok: 'bg-zinc-100 text-zinc-700 border-zinc-300',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return iso;
  }
};

export const ArchivePage = () => {
  const { api } = useAuth();
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  const { clients, byId: clientsById } = useClients();
  const [busyId, setBusyId] = useState(null);
  const [viewingPost, setViewingPost] = useState(null);

  const fetchArchived = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('archived', 'true');
      params.append('limit', '200');
      if (filterPlatform !== 'all') params.append('platform', filterPlatform);
      if (filterClient && filterClient !== 'all') params.append('client_id', filterClient);
      const res = await api.get(`/posts?${params.toString()}`);
      setPosts(res.data.posts || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error('Failed to load archive');
    } finally {
      setLoading(false);
    }
  }, [api, filterPlatform, filterClient]);

  useEffect(() => { fetchArchived(); }, [fetchArchived]);

  const restore = async (post) => {
    if (!window.confirm('Restore this post to your active feed?')) return;
    setBusyId(post.id);
    try {
      await api.post(`/posts/${post.id}/restore`);
      toast.success('Post restored');
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (post) => {
    if (!window.confirm('Permanently delete this archived post? This cannot be undone.')) return;
    setBusyId(post.id);
    try {
      await api.delete(`/posts/${post.id}`);
      toast.success('Post deleted');
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  const filtered = posts.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (p.post_title || '').toLowerCase().includes(q) ||
      (p.caption || '').toLowerCase().includes(q) ||
      (p.topic || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6" data-testid="archive-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
          <Archive className="w-6 h-6 text-indigo-600" />
          Archive
        </h1>
        <p className="text-zinc-500 text-sm mt-0.5">
          Successfully published posts live here. View, restore, or remove.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            placeholder="Search archived posts…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="archive-search-input"
          />
        </div>
        <Select value={filterPlatform} onValueChange={setFilterPlatform}>
          <SelectTrigger className="w-full sm:w-44" data-testid="archive-platform-filter">
            <SelectValue placeholder="Platform" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All platforms</SelectItem>
            <SelectItem value="linkedin">LinkedIn</SelectItem>
            <SelectItem value="facebook">Facebook</SelectItem>
            <SelectItem value="instagram">Instagram</SelectItem>
            <SelectItem value="bluesky">Bluesky</SelectItem>
            <SelectItem value="youtube">YouTube</SelectItem>
          </SelectContent>
        </Select>
        <ClientFilterPill
          value={filterClient}
          onChange={setFilterClient}
          clients={clients}
          width="w-full sm:w-44"
        />
      </div>

      {/* Stats summary */}
      <div className="text-xs text-zinc-500" data-testid="archive-count">
        {loading ? 'Loading…' : `${filtered.length} of ${total} archived post${total === 1 ? '' : 's'}`}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed border-zinc-200">
          <CardContent className="p-12 text-center">
            <Archive className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-zinc-700">No archived posts yet</p>
            <p className="text-xs text-zinc-500 mt-1">
              Posts auto-archive once they’ve been successfully published.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((post) => {
            const TypeIcon = TYPE_ICONS[post.post_type] || FileText;
            const platformClass = PLATFORM_COLORS[(post.platform || '').toLowerCase()] || 'bg-zinc-100 text-zinc-700 border-zinc-200';
            const heroMedia = (post.media || []).find((m) => m.type === 'image');
            return (
              <Card
                key={post.id}
                className="border-zinc-200 hover:shadow-md transition-shadow"
                data-testid={`archive-card-${post.id}`}
              >
                {heroMedia ? (
                  <div className="aspect-video bg-zinc-100 rounded-t-md overflow-hidden">
                    <img
                      src={heroMedia.url}
                      alt={post.post_title || 'archived post'}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                ) : null}
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between gap-2">
                    <Badge variant="outline" className={platformClass}>
                      {(post.platform || 'unknown').replace('_', ' ')}
                    </Badge>
                    <span className="text-[10px] text-zinc-400 flex items-center gap-1">
                      <TypeIcon className="w-3 h-3" />
                      {post.post_type || 'post'}
                    </span>
                  </div>
                  {post.client_id && (
                    <div className="mt-1.5">
                      <ClientBadge clientId={post.client_id} clientsById={clientsById} />
                    </div>
                  )}
                  <h3 className="text-sm font-semibold text-zinc-900 leading-tight line-clamp-2 mt-2">
                    {post.post_title || post.topic || 'Untitled post'}
                  </h3>
                </CardHeader>
                <CardContent className="space-y-3 pt-0">
                  <p className="text-xs text-zinc-600 line-clamp-3">{post.caption || '—'}</p>
                  <div className="text-[11px] text-zinc-500 space-y-0.5">
                    <div>Published: {formatDate(post.published_at)}</div>
                    <div>Archived: {formatDate(post.archived_at)}</div>
                  </div>
                  <div className="flex items-center gap-2 pt-1 flex-wrap">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setViewingPost(post)}
                      data-testid={`archive-view-${post.id}`}
                    >
                      <Eye className="w-3.5 h-3.5 mr-1" />
                      View
                    </Button>
                    {post.published_url && (
                      <a
                        href={post.published_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline px-2 py-1"
                        data-testid={`archive-open-${post.id}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Live
                      </a>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => restore(post)}
                      disabled={busyId === post.id}
                      data-testid={`archive-restore-${post.id}`}
                    >
                      <RotateCcw className="w-3.5 h-3.5 mr-1" />
                      Restore
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:bg-red-50 hover:text-red-700 ml-auto"
                      onClick={() => remove(post)}
                      disabled={busyId === post.id}
                      data-testid={`archive-delete-${post.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <ViewPostDialog
        post={viewingPost}
        open={!!viewingPost}
        onOpenChange={(o) => !o && setViewingPost(null)}
        onEdit={() => setViewingPost(null)}
        onSchedule={() => setViewingPost(null)}
      />
    </div>
  );
};

export default ArchivePage;
